import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { Bug, RefreshCw, Plus } from 'lucide-react';
import firmService from '../../services/firmService';
import devIssueService from '../../services/devIssueService';
import KanbanLane from '../../components/admin/dev-issues/KanbanLane';
import IssueCard from '../../components/admin/dev-issues/IssueCard';
import IssueCreateModal from '../../components/admin/dev-issues/IssueCreateModal';
import IssueDetailModal from '../../components/admin/dev-issues/IssueDetailModal';
import Notification from '../../components/common/Notification';
import useNotification from '../../hooks/useNotification';

const LANE_ORDER = ['backlog', 'up_next', 'in_progress', 'done', 'rejected'];

function groupByStatus(issues) {
  const lanes = {};
  LANE_ORDER.forEach(s => { lanes[s] = []; });
  issues.forEach(issue => {
    const lane = lanes[issue.status] || lanes['backlog'];
    lane.push(issue);
  });
  // Sort each lane by sort_order
  LANE_ORDER.forEach(s => {
    lanes[s].sort((a, b) => a.sort_order - b.sort_order);
  });
  return lanes;
}

const DevIssueBoard = () => {
  const navigate = useNavigate();
  const { notification, showSuccess, showError, hideNotification } = useNotification();

  const [issues, setIssues] = useState([]);
  const [lanes, setLanes] = useState(() => { const l = {}; LANE_ORDER.forEach(s => { l[s] = []; }); return l; });
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);

  const [filters, setFilters] = useState({ type: '', priority: '', search: '' });
  const searchRef = useRef(null);

  const [showCreate, setShowCreate] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [activeIssue, setActiveIssue] = useState(null); // DnD active

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  // --- Auth check ---
  const authChecked = useRef(false);
  useEffect(() => {
    if (authChecked.current) return;
    authChecked.current = true;
    firmService.checkSuperAdmin()
      .then(result => {
        if (!result.is_super_admin) navigate('/');
        else setAuthorized(true);
      })
      .catch(() => navigate('/'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Fetch issues ---
  const fetchIssues = useCallback(async () => {
    const params = {};
    if (filters.type) params.issue_type = filters.type;
    if (filters.priority) params.priority = filters.priority;
    if (filters.search) params.search = filters.search;
    try {
      const res = await devIssueService.getAll(params);
      const data = res.data || [];
      setIssues(data);
      setLanes(groupByStatus(data));
    } catch {
      showError('Failed to load issues');
    } finally {
      setLoading(false);
    }
  }, [filters, showError]);

  useEffect(() => {
    if (authorized) fetchIssues();
  }, [authorized, fetchIssues]);

  // --- DnD ---
  const findLane = (id) => {
    if (LANE_ORDER.includes(id)) return id;
    for (const laneId of LANE_ORDER) {
      if (lanes[laneId].some(i => i.id === id)) return laneId;
    }
    return null;
  };

  const handleDragStart = ({ active }) => {
    const allIssues = LANE_ORDER.flatMap(l => lanes[l]);
    setActiveIssue(allIssues.find(i => i.id === active.id) || null);
  };

  const handleDragOver = ({ active, over }) => {
    if (!over) return;
    const fromLane = findLane(active.id);
    const toLane = findLane(over.id);
    if (!fromLane || !toLane || fromLane === toLane) return;

    setLanes(prev => {
      const newLanes = { ...prev };
      const fromItems = [...prev[fromLane]];
      const toItems = [...prev[toLane]];
      const idx = fromItems.findIndex(i => i.id === active.id);
      if (idx === -1) return prev;
      const [moved] = fromItems.splice(idx, 1);
      moved.status = toLane;
      toItems.push(moved);
      return { ...newLanes, [fromLane]: fromItems, [toLane]: toItems };
    });
  };

  const handleDragEnd = async ({ active, over }) => {
    setActiveIssue(null);
    if (!over) return;

    const toLane = findLane(over.id);
    if (!toLane) return;

    const laneItems = lanes[toLane];
    const overIdx = laneItems.findIndex(i => i.id === over.id);
    const activeIdx = laneItems.findIndex(i => i.id === active.id);

    let newOrder = laneItems;
    if (activeIdx !== -1 && overIdx !== -1 && activeIdx !== overIdx) {
      newOrder = arrayMove(laneItems, activeIdx, overIdx);
      setLanes(prev => ({ ...prev, [toLane]: newOrder }));
    }

    // Persist
    const movedIssue = laneItems.find(i => i.id === active.id) || newOrder.find(i => i.id === active.id);
    if (!movedIssue) return;

    const newSortOrder = overIdx !== -1 ? overIdx * 1000 : newOrder.indexOf(movedIssue) * 1000;
    try {
      await devIssueService.move(active.id, toLane, newSortOrder);
      // Bulk reorder the lane to normalize positions
      const reorderItems = newOrder.map((issue, idx) => ({ id: issue.id, sort_order: idx * 1000 }));
      await devIssueService.reorder(reorderItems);
    } catch {
      showError('Failed to save position');
      fetchIssues(); // re-fetch to restore
    }
  };

  // --- Issue CRUD ---
  const handleCreate = (newIssue) => {
    setIssues(prev => [...prev, newIssue]);
    setLanes(prev => {
      const backlog = [...(prev.backlog || []), newIssue];
      backlog.sort((a, b) => a.sort_order - b.sort_order);
      return { ...prev, backlog };
    });
    showSuccess('Issue created');
  };

  const handleUpdate = (updated) => {
    const newIssues = issues.map(i => i.id === updated.id ? updated : i);
    setIssues(newIssues);
    setLanes(groupByStatus(newIssues));
    if (selectedIssue?.id === updated.id) setSelectedIssue(updated);
    showSuccess('Issue updated');
  };

  const handleDelete = (deletedId) => {
    const newIssues = issues.filter(i => i.id !== deletedId);
    setIssues(newIssues);
    setLanes(groupByStatus(newIssues));
    showSuccess('Issue deleted');
  };

  const openDetail = async (issue) => {
    try {
      const res = await devIssueService.getById(issue.id);
      setSelectedIssue(res.data);
    } catch {
      setSelectedIssue(issue);
    }
  };

  if (!authorized) return null;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 pt-6 pb-4 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-xl">
              <Bug className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Dev Issues</h1>
              <p className="text-sm text-gray-500">{issues.length} issue{issues.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchIssues}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-1.5 px-4 py-2 bg-yellow-500 text-white text-sm font-medium rounded-lg hover:bg-yellow-600 transition-colors"
            >
              <Plus className="h-4 w-4" />
              New Issue
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3">
          <input
            ref={searchRef}
            type="text"
            value={filters.search}
            onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
            placeholder="Search issues…"
            className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-yellow-400 w-56"
          />
          <select
            value={filters.type}
            onChange={e => setFilters(f => ({ ...f, type: e.target.value }))}
            className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-white"
          >
            <option value="">All types</option>
            <option value="bug">Bug</option>
            <option value="feature">Feature</option>
            <option value="improvement">Improvement</option>
          </select>
          <select
            value={filters.priority}
            onChange={e => setFilters(f => ({ ...f, priority: e.target.value }))}
            className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-white"
          >
            <option value="">All priorities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          {(filters.type || filters.priority || filters.search) && (
            <button
              onClick={() => setFilters({ type: '', priority: '', search: '' })}
              className="text-xs text-gray-400 hover:text-gray-600 underline"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Board */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
            <p className="text-sm text-gray-500">Loading board…</p>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-x-auto px-6 py-5">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            <div className="flex gap-4 h-full">
              {LANE_ORDER.map(laneId => (
                <KanbanLane
                  key={laneId}
                  laneId={laneId}
                  issues={lanes[laneId] || []}
                  onCardClick={openDetail}
                  onAddClick={() => setShowCreate(true)}
                />
              ))}
            </div>

            <DragOverlay>
              {activeIssue && (
                <IssueCard issue={activeIssue} onClick={() => {}} />
              )}
            </DragOverlay>
          </DndContext>
        </div>
      )}

      {/* Modals */}
      <IssueCreateModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onCreate={handleCreate}
      />
      <IssueDetailModal
        isOpen={!!selectedIssue}
        issue={selectedIssue}
        onClose={() => setSelectedIssue(null)}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
      />

      <Notification
        isOpen={notification.isOpen}
        onClose={hideNotification}
        title={notification.title}
        message={notification.message}
        variant={notification.variant}
        duration={notification.duration}
      />
    </div>
  );
};

export default DevIssueBoard;
